import { useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { LinkedinLogo } from "@phosphor-icons/react";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Photo, Seo } from "../../kit.jsx";
import { coreTeamGroups } from "../../data/content.js";
import { ArrowLink, Btn, Kicker } from "./shared.jsx";
import "./about.css";

const initials = (name) =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2);

function LinkedInLink({ member }) {
  if (!member.linkedin) return null;
  return (
    <a className="ab-li" href={member.linkedin} target="_blank" rel="noreferrer" aria-label={`${member.name} on LinkedIn`}>
      <LinkedinLogo weight="fill" aria-hidden="true" />
    </a>
  );
}

const WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
const countWord = (n) => WORDS[n] ?? String(n);
const listJoin = (items) => (items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);
const firstName = (name) => name.split(" ")[0];

function FounderCard({ member, index }) {
  const reduceMotion = useReducedMotion();
  const [title, short] = member.role.split(", ");
  return (
    <motion.li
      className="ab-person"
      initial={reduceMotion ? false : { opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.9, delay: index * 0.08, ease: EASE_OUT }}
    >
      <div className={`ab-person__media ${member.photo ? "has-photo" : ""}`}>
        {member.photo ? (
          <Photo src={member.photo} alt={`Portrait of ${member.name}`} width={900} height={900} sizes="(max-width: 767px) 50vw, 25vw" />
        ) : (
          <span className="ab-person__mono" aria-hidden="true">{initials(member.name)}</span>
        )}
        {short && <span className="ab-person__tag">{short}</span>}
      </div>
      <div className="ab-person__name">
        <h3>{member.name}</h3>
        <LinkedInLink member={member} />
      </div>
      <p>{title}{short ? `, ${short}` : ""}</p>
    </motion.li>
  );
}

function Directory({ groups }) {
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState("All");
  const people = useMemo(
    () => groups.flatMap((group) => group.members.map((member) => ({ ...member, group: group.label }))),
    [groups],
  );
  const visible = active === "All" ? people : people.filter((person) => person.group === active);
  const chips = [["All", people.length], ...groups.map((group) => [group.label, group.members.length])];
  const layoutTransition = { duration: reduceMotion ? 0 : 0.5, ease: EASE_OUT };

  return (
    <section className="ab-dir" aria-labelledby="ab-dir-title">
      <header className="ab-dir__head">
        <MaskWords id="ab-dir-title" className="ab-h2" text="Find who does what." />
        <p className="ab-body">{countWord(groups.length)} working {groups.length === 1 ? "group sits" : "groups sit"} under the founding team. Pick one to see who is responsible for it.</p>
      </header>
      <div className="ab-dir__chips" role="group" aria-label="Filter by working group">
        {chips.map(([label, count]) => (
          <button
            key={label}
            type="button"
            className={`ab-chip ${active === label ? "is-active" : ""}`}
            aria-pressed={active === label}
            onClick={() => setActive(label)}
          >
            {active === label && !reduceMotion && (
              <motion.span className="ab-chip__bg" layoutId="ab-chip-bg" transition={layoutTransition} aria-hidden="true" />
            )}
            <span className="ab-chip__label">{label}</span>
            <span className="ab-chip__count">{count}</span>
          </button>
        ))}
      </div>
      <p className="ab-dir__status" aria-live="polite">
        Showing {visible.length} of {people.length}
      </p>
      <motion.ul className="ab-dir__list" layout={!reduceMotion} transition={layoutTransition}>
        <AnimatePresence mode="popLayout" initial={false}>
          {visible.map((person) => (
            <motion.li
              key={person.name}
              layout={!reduceMotion}
              initial={reduceMotion ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, transition: { duration: 0.18 } }}
              transition={layoutTransition}
            >
              <span className="ab-dir__who">
                <strong>{person.name}</strong>
                <LinkedInLink member={person} />
              </span>
              <span className="ab-dir__role">{person.role}</span>
              <span className="ab-dir__group">{person.group}</span>
            </motion.li>
          ))}
        </AnimatePresence>
      </motion.ul>
    </section>
  );
}

function areasLine(groups) {
  const areas = listJoin(groups.map((group) => group.label.toLowerCase()));
  return `Each area has someone responsible: ${areas}.`;
}

function founderLine(members) {
  const cofounders = members.filter((m) => /co-founder/i.test(m.role)).map((m) => firstName(m.name));
  const others = members.filter((m) => !/co-founder/i.test(m.role)).map((m) => firstName(m.name));
  const parts = [];
  if (cofounders.length) parts.push(`${listJoin(cofounders)} started GRID in December 2025.`);
  if (others.length) parts.push(`${listJoin(others)} ${others.length === 1 ? "is" : "are"} on the founding board.`);
  return parts.join(" ");
}

export default function TeamPage() {
  const reduceMotion = useReducedMotion();
  const [founders, ...groups] = coreTeamGroups;
  const total = coreTeamGroups.reduce((sum, group) => sum + group.members.length, 0);
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });

  return (
    <PageFrame className="rd-page ab-page ab-page--team">
      <Seo
        title="Project GRID Team"
        path="/team"
        description="Meet the Project GRID team across leadership, partnerships, engineering, outreach, community and creative work."
      />
      <main>
        <section className="ab-thero" aria-labelledby="ab-thero-title">
          <motion.div {...appear(0)}><Kicker>Our team</Kicker></motion.div>
          <div className="ab-thero__row">
            <MaskWords as="h1" id="ab-thero-title" className="ab-display ab-display--mid" text={`${total} students run GRID.`} onMount delay={0.08} />
            <motion.div className="ab-thero__copy" {...appear(0.4)}>
              <p className="ab-lede">
                Every name here has a named job. {areasLine(groups)}
              </p>
              <div className="gh-actions">
                <Btn to="/student-opportunities/team">Join the team</Btn>
                <Btn href="#directory" variant="ghost">Find a person</Btn>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="ab-founders" aria-labelledby="ab-founders-title">
          <div className="ab-founders__head">
            <h2 id="ab-founders-title" className="ab-h3">Founding team</h2>
            <p>{founderLine(founders.members)}</p>
          </div>
          <ol className="ab-founders__list">
            {founders.members.map((member, index) => (
              <FounderCard key={member.name} member={member} index={index} />
            ))}
          </ol>
        </section>

        <div id="directory">
          <Directory groups={groups} />
        </div>

        <section className="ab-principle" aria-labelledby="ab-principle-title">
          <div className="ab-principle__copy">
            <MaskWords id="ab-principle-title" className="ab-h2" text="A title should show who owns the work." />
            <p className="ab-body">
              This directory lists named responsibilities, not empty positions.
            </p>
            <ArrowLink to="/governance">How we're run</ArrowLink>
          </div>
          <ClipPhoto
            className="ab-principle__photo"
            src="/images/grid-photo-09-p8.jpg"
            alt="Three GRID builders standing together inside a manufacturing facility"
            caption="Builders together inside a manufacturing facility."
            sizes="(max-width: 900px) 100vw, 50vw"
          />
        </section>

        <section className="ab-tjoin" aria-labelledby="ab-tjoin-title">
          <MaskWords id="ab-tjoin-title" className="ab-h2" text="Want a named job here?" />
          <p className="ab-body">Students join to run a real part of GRID: a programme, the technology, partnerships, community or design.</p>
          <div className="gh-actions">
            <Btn to="/student-opportunities/team">Join the team</Btn>
            <Btn to="/student-opportunities/internships" variant="ghost">Internships</Btn>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}
